import React from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDay } from '@/types/schedule';
import { CalendarEventData, WeekendSettingsResponse } from '@/types/billing-calendar';

interface BillingCalendarProps {
  currentDate: Date;
  calendarDays: CalendarDay[];
  calendarData: CalendarEventData | null;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onCurrentMonth: () => void;
  onDayClick?: (day: number) => void;
  selectedDay?: number | null;
  weekendSettings?: WeekendSettingsResponse | null;
}

const BillingCalendar: React.FC<BillingCalendarProps> = ({
  currentDate,
  calendarDays,
  calendarData,
  onPreviousMonth,
  onNextMonth,
  onCurrentMonth,
  onDayClick,
  selectedDay,
  weekendSettings
}) => {
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const monthName = monthNames[currentDate.getMonth()];
  const year = currentDate.getFullYear();

  // Agrupar dias em semanas
  const weeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  // Função para verificar se um dia é sábado ou domingo bloqueado
  const isWeekendBlocked = (day: CalendarDay): boolean => {
    if (!day.isCurrentMonth || !weekendSettings) return false;
    
    // Calcular o dia da semana (0 = domingo, 6 = sábado)
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day.day);
    const dayOfWeek = date.getDay();
    
    // Verificar se é domingo bloqueado
    if (dayOfWeek === 0 && weekendSettings.block_sunday) {
      return true;
    }
    
    // Verificar se é sábado bloqueado
    if (dayOfWeek === 6 && weekendSettings.block_saturday) {
      return true;
    }
    
    return false;
  };

  // Função para determinar a cor do dia baseado no estado (design minimalista)
  const getDayStyle = (day: CalendarDay) => {
    if (!day.isCurrentMonth) {
      return 'text-gray-400 bg-transparent border-transparent';
    }

    const dayData = calendarData?.days.find(d => d.day === day.day);
    const isBlocked = dayData?.isBlocked || false;
    const hasRescheduled = dayData?.hasRescheduled || false;
    const isHoliday = dayData?.isHoliday || false;
    const isWeekendBlockedDay = isWeekendBlocked(day);

    // Dia selecionado - borda azul destacada (como na imagem de referência)
    if (selectedDay === day.day) {
      return isHoliday 
        ? 'bg-yellow-50 border-2 border-blue-500 text-gray-900 shadow-sm' 
        : 'bg-white border-2 border-blue-500 text-gray-900 shadow-sm';
    }

    // Dia bloqueado manualmente - fundo vermelho claro
    if (isBlocked) {
      return 'bg-red-50 border border-red-200 text-gray-700';
    }

    // Sábado/domingo bloqueado - fundo cinza claro
    if (isWeekendBlockedDay) {
      return 'bg-gray-100 border border-gray-300 text-gray-600';
    }

    // Dia com reagendamento - fundo azul claro
    if (hasRescheduled) {
      return 'bg-blue-50 border border-blue-200 text-gray-700';
    }

    // Dia feriado - fundo amarelo claro com borda laranja
    if (isHoliday) {
      return 'bg-yellow-50 border border-orange-300 text-gray-900';
    }

    // Dia com eventos - borda verde claro (minimalista, como na imagem)
    if (day.hasEvents) {
      return 'bg-white border border-green-300 text-gray-900';
    }

    // Dia atual - borda azul sutil (se não for selecionado)
    if (day.isToday && selectedDay !== day.day) {
      return 'bg-white border border-blue-300 text-gray-900';
    }

    // Dia normal - borda verde muito sutil (como na imagem de referência)
    return 'bg-white border border-green-200 text-gray-700';
  };

  return (
    <Card className="w-full border-0 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between mb-3">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <CalendarIcon className="h-5 w-5 text-gray-600" />
            Cronograma de Envios
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onCurrentMonth}
              className="text-xs bg-gray-50 hover:bg-gray-100 border-gray-200"
            >
              Hoje
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onPreviousMonth}
              className="p-2 bg-gray-50 hover:bg-gray-100 border-gray-200"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onNextMonth}
              className="p-2 bg-gray-50 hover:bg-gray-100 border-gray-200"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* Mês e ano em destaque */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            {monthName} {year}
          </h2>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Cabeçalho dos dias da semana */}
        <div className="grid grid-cols-7 gap-0 mb-1">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-center text-sm font-medium text-gray-900 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Grid do calendário - design minimalista */}
        <div className="grid grid-cols-7 gap-0.5">
          {weeks.map((week, weekIndex) => (
            <React.Fragment key={weekIndex}>
              {week.map((day, dayIndex) => (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={`
                    relative aspect-square flex items-center justify-center rounded-md transition-all duration-150
                    ${getDayStyle(day)}
                    ${day.isCurrentMonth ? 'cursor-pointer hover:shadow-sm hover:scale-105' : 'cursor-default'}
                  `}
                  onClick={() => {
                    if (day.isCurrentMonth && onDayClick) {
                      onDayClick(day.day);
                    }
                  }}
                  title={(() => {
                    const dayData = calendarData?.days.find(d => d.day === day.day);
                    if (dayData?.isHoliday && dayData?.holidayName) {
                      return dayData.holidayName;
                    }
                    return undefined;
                  })()}
                >
                  {/* Número do dia - centralizado e limpo */}
                  <div className="flex flex-col items-center justify-center">
                    <span className={`text-sm ${selectedDay === day.day ? 'font-bold' : 'font-medium'}`}>
                      {day.day}
                    </span>
                    {/* Indicadores */}
                    {(() => {
                      const dayData = calendarData?.days.find(d => d.day === day.day);
                      const isWeekendBlockedDay = isWeekendBlocked(day);
                      
                      // Indicador de feriado
                      if (dayData?.isHoliday) {
                        return (
                          <span className="text-[8px] text-orange-600 font-semibold mt-0.5 leading-none">
                            🎉
                          </span>
                        );
                      }
                      
                      // Indicador de sábado/domingo bloqueado
                      if (isWeekendBlockedDay) {
                        return (
                          <span className="text-[8px] text-gray-500 font-semibold mt-0.5 leading-none">
                            🚫
                          </span>
                        );
                      }
                      
                      return null;
                    })()}
                  </div>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>

        {/* Legenda minimalista - removida ou muito discreta */}
        {/* A legenda pode ser mostrada em um tooltip ou removida completamente para design mais limpo */}
      </CardContent>
    </Card>
  );
};

export default BillingCalendar;

